// Student payment permission gate (student-self-signup-prd.md §19, D2).
//
// Before an independent student's first purchase, the backend refuses every
// student checkout route with 403 STUDENT_PAYMENT_PERMISSION_REQUIRED until the
// student confirms once that they may use the payment card (and, under 18,
// that a parent or guardian said yes). The refusal happens before the handler
// runs, so nothing was bought and the request is safe to send again.
//
// `api()` calls `requestStudentPaymentPermission()` when it sees that refusal.
// The Learn shell registers one handler (`StudentPaymentPermissionHost`) that
// opens the permission step and resolves `true` once the student confirmed, so
// `api()` sends the same request again and the page continues. With no handler
// (Portal, tests, a non-student) it resolves `false` and the refusal reaches
// the page as a normal ApiError.
//
// This module imports nothing, so `api.ts` can use it without an import cycle.

export const STUDENT_PAYMENT_PERMISSION_REQUIRED = 'STUDENT_PAYMENT_PERMISSION_REQUIRED';

export type StudentPaymentPermissionHandler = () => Promise<boolean>;

let handler: StudentPaymentPermissionHandler | null = null;
let pending: Promise<boolean> | null = null;

/**
 * Register the handler that shows the permission step. Returns an unregister
 * function that only removes this same handler.
 */
export function setStudentPaymentPermissionHandler(
  next: StudentPaymentPermissionHandler,
): () => void {
  handler = next;
  return () => {
    if (handler === next) handler = null;
  };
}

/**
 * Ask the student to confirm. Requests that are refused at the same time share
 * one step, so the student sees it once. Resolves `false` when no handler is
 * registered or the student closes the step.
 */
export function requestStudentPaymentPermission(): Promise<boolean> {
  if (!handler) return Promise.resolve(false);
  if (pending) return pending;
  const run = handler()
    .catch(() => false)
    .finally(() => {
      pending = null;
    });
  pending = run;
  return run;
}
